// Página de Política de DMCA y Contenido de Terceros de la Plataforma
import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { Scale, Calendar, Mail } from 'lucide-react'
import client from '../api/client'
import Card from '../components/ui/Card'

export default function DMCAPolicy() {
  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      const response = await client.get('/settings')
      return response.data?.datos || {}
    },
    staleTime: 5 * 60 * 1000,
  })

  const nombreSistema = settings?.nombre_sistema || 'SubiGol'
  const emailContacto = settings?.email_contacto || 'soporte@apuestaneta.com'

  return (
    <div className="py-8 px-4 sm:py-12 sm:px-6 lg:px-8 max-w-4xl mx-auto space-y-8 animate-fade-in font-body">
      {/* Encabezado */}
      <div className="flex items-center gap-4 border-b border-border-subtle pb-6">
        <div className="p-3 rounded-2xl bg-cyan/10 border border-cyan/20 text-cyan">
          <Scale className="w-8 h-8" />
        </div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-text-primary">
            DMCA y Contenido de Terceros
          </h1>
          <p className="text-xs text-text-muted mt-1 flex items-center gap-1.5 font-mono">
            <Calendar className="w-3.5 h-3.5" /> Última actualización: 24 de junio de 2026
          </p>
        </div>
      </div>

      <div className="space-y-6 text-sm text-text-secondary leading-relaxed">
        <p>
          Esta Política de Derechos de Autor (DMCA) describe los términos y condiciones bajo los cuales <strong className="font-semibold text-text-primary">{nombreSistema}</strong> aborda las notificaciones de presuntas infracciones de derechos de autor en relación con los contenidos embebidos o enlazados en nuestra plataforma.
        </p>

        {/* Sección 1 */}
        <section className="space-y-2">
          <h3 className="font-display font-bold text-base text-text-primary uppercase tracking-wider">
            1. Exención de Responsabilidad de Contenido (Iframes y Enlaces)
          </h3>
          <p>
            <strong className="font-semibold text-text-primary">{nombreSistema}</strong> funciona exclusivamente como un directorio interactivo de información deportiva. 
            <strong className="text-text-primary font-medium"> No alojamos, transmitimos, almacenamos ni retransmitimos</strong> directamente ninguna señal de video, transmisión televisiva, archivos multimedia o transmisiones en vivo (streaming) en nuestros servidores. 
          </p>
          <p>
            Cualquier reproducción o visualización de canales en vivo en nuestra sección de transmisiones se realiza mediante la inserción de reproductores externos (iframes), los cuales son provistos por terceros ajenos a nuestra administración (plataformas públicas de video, páginas web externas u otros servidores de streaming). Por ende, el control absoluto sobre la legalidad y los derechos de dicha señal recae de forma exclusiva sobre el proveedor que aloja la señal original de origen.
          </p>
        </section>

        {/* Sección 2 */}
        <section className="space-y-2">
          <h3 className="font-display font-bold text-base text-text-primary uppercase tracking-wider">
            2. Procedimiento para Reportar Infracciones (Notificación DMCA)
          </h3>
          <p>
            Respetamos los derechos de propiedad intelectual de los demás. Si usted es titular de derechos de autor, o está autorizado para actuar en nombre de uno, y considera de buena fe que cualquier señal enlazada o embebida en <strong className="font-semibold text-text-primary">{nombreSistema}</strong> infringe sus derechos de autor, puede presentar una notificación de infracción enviándonos una comunicación por escrito que incluya la siguiente información:
          </p>
          <ul className="list-disc list-inside pl-4 space-y-1.5 mt-2">
            <li>
              Identificación clara del trabajo protegido por derechos de autor que alega que ha sido infringido.
            </li>
            <li>
              Identificación del material o enlace en nuestra plataforma que desea reportar, con la información suficiente para permitirnos localizarlo (por ejemplo, el nombre específico del canal o evento dentro del sistema).
            </li>
            <li>
              Su información de contacto, incluyendo nombre completo, dirección, número de teléfono y dirección de correo electrónico.
            </li>
            <li>
              Una declaración que indique que usted tiene la creencia de buena fe de que el uso del material de la manera reclamada no está autorizado por el propietario de los derechos de autor, su agente o la ley.
            </li>
            <li>
              Una declaración de que la información contenida en la notificación es exacta y, bajo pena de perjurio, de que usted está autorizado a actuar en nombre del titular de un derecho exclusivo que presuntamente se ha infringido.
            </li>
            <li>
              Firma física o electrónica del titular de los derechos de autor o de la persona autorizada para actuar en su nombre.
            </li>
          </ul>
        </section>

        {/* Sección 3 */}
        <section className="space-y-2">
          <h3 className="font-display font-bold text-base text-text-primary uppercase tracking-wider">
            3. Acción de Retiro de Enlaces
          </h3>
          <p>
            Una vez recibida una notificación DMCA válida que cumpla con los requisitos descritos anteriormente, <strong className="font-semibold text-text-primary">{nombreSistema}</strong> procederá a revisar la solicitud en un plazo máximo de 48 horas hábiles. En caso de corroborar la validez del reclamo, nuestra acción consistirá en <strong className="text-text-primary font-medium">deshabilitar o remover el enlace/iframe</strong> que conduce a la señal externa infractora dentro de nuestro sistema.
          </p>
          <p>
            Tenga en cuenta que retirar el enlace de nuestra aplicación no elimina el contenido original de la web ni del servidor de streaming que lo aloja. Si desea eliminar el video o la señal de internet de forma permanente, le recomendamos contactar directamente al proveedor de servicios de hosting que almacena el material original.
          </p>
        </section>

        {/* Sección 4 */}
        <section className="space-y-2">
          <h3 className="font-display font-bold text-base text-text-primary uppercase tracking-wider">
            4. Contacto de Soporte Legal
          </h3>
          <p>
            Para hacernos llegar cualquier reporte, solicitud de retiro o consulta legal relacionada con los derechos de autor, puede escribirnos directamente a nuestro correo de contacto y soporte:
          </p>
        </section>
      </div>

      {/* Caja de contacto por correo */}
      <Card className="p-4 bg-navy-dark/40 border border-border-subtle rounded-xl flex items-center gap-3">
        <Mail className="w-6 h-6 text-cyan shrink-0 animate-pulse" />
        <div className="text-xs text-text-muted leading-relaxed font-sans">
          Para notificaciones formales de derechos de autor, envíe su correo electrónico detallado a: 
          <a href={`mailto:${emailContacto}`} className="text-cyan hover:underline font-semibold font-mono ml-1.5 text-sm">{emailContacto}</a>
        </div>
      </Card>
    </div>
  )
}
